import React, { useState } from 'react';
import './ReportV5.css';
import ReportIcon from './ReportIcons';
import ReportV5Radar from './ReportV5Radar';
import { TIER_INTENSITY } from '../../constants/reportV5Labels';
import { downloadReportV5PDF } from '../../api/reports';

/**
 * V5 粗略报告主组件。
 * 入参：
 *  - vm:            adapter 转出的 view model（结构见 adapters/reportV5Adapter.js）
 *  - assessmentId:  当前测评 id（'preview' 时 PDF 下载按钮禁用）
 *  - onUnlock:      () => void   点击"解锁详细报告" CTA 时触发
 *  - printMode:     boolean  Playwright PDF 印刷模式 —— 根容器加 .print-mode 类
 *                            供 print.css 接管样式；CTA 卡片 / PDF 下载按钮整体
 *                            不渲染（PDF 内部不该有"下载 PDF"按钮）。
 *
 * 3 个 Page 顺序流式排列（同一卷轴）：结果总览 / 能力结构 / 行动建议。
 * 顶部 fallback 提示由 vm.is_fallback 触发；PDF 下载按钮调用 api/reports.downloadReportV5PDF。
 */
export default function ReportV5({ vm, assessmentId, onUnlock, onBack, printMode = false }) {
  return (
    <div className={`v5r-root${printMode ? ' print-mode' : ''}`}>
      <div className="v5r-container">
        {onBack && !printMode && <BackBar onBack={onBack} />}
        {vm.is_fallback && <FallbackBanner />}
        <Page1 vm={vm} />
        <Page2 vm={vm} />
        <Page3 vm={vm} />
        {!printMode && (
          <DetailedCTA
            unlocked={vm.detailed.unlocked}
            onUnlock={onUnlock}
            assessmentId={assessmentId}
          />
        )}
      </div>
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          background: '#fff',
          border: '1px solid var(--v5-line)',
          borderRadius: 10,
          color: 'var(--v5-ink-2)',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(15, 35, 80, 0.04)',
        }}
      >
        <ReportIcon name="arrow-left" size={16} />
        <span>返回历史记录</span>
      </button>
    </div>
  );
}

// ===========================================================================
// 共享子组件
// ===========================================================================

function FallbackBanner() {
  return (
    <div className="v5r-fallback-banner">
      <ReportIcon name="lightbulb" size={18} />
      <div>
        <b>降级版本：</b>本次报告由后端 fallback 模板生成（LLM 输出未通过硬校验）。
        分数与排名均为真实计算结果，文案为模板兜底，建议联系管理员重新生成。
      </div>
    </div>
  );
}

function PageHeader({ section, subtitle, icon }) {
  return (
    <header className="v5r-page-head" style={{ borderBottom: 'none', marginBottom: 0 }}>
      <div>
        <h1>
          AI胜任力测评报告（基础版）
          <span className="v5r-divider">|</span>
          <span className="v5r-section-name">{section}</span>
        </h1>
        <div className="v5r-sub">{subtitle}</div>
      </div>
      <div className="v5r-head-icon">
        <ReportIcon name={icon} size={36} strokeWidth={1.3} />
      </div>
    </header>
  );
}

function MetaBar({ meta }) {
  return (
    <div className="v5r-meta">
      <MetaItem icon="users" label="测评人" value={meta.nickname} />
      {meta.position && <MetaItem icon="user-check" label="岗位" value={meta.position} />}
      <MetaItem icon="award" label="版本" value={meta.version_label} />
      <MetaItem icon="clipboard" label="作答" value={`${meta.answers_done}/${meta.answers_total}`} />
      <MetaItem icon="book-open" label="生成时间" value={meta.generated_at} />
    </div>
  );
}

function MetaItem({ icon, label, value }) {
  return (
    <span className="v5r-meta-item">
      <span className="v5r-meta-icon"><ReportIcon name={icon} size={13} /></span>
      <span className="v5r-meta-label">{label}：</span>
      <span className="v5r-meta-val">{value || '—'}</span>
    </span>
  );
}

// ===========================================================================
// Page 1 · 结果总览
// ===========================================================================
function Page1({ vm }) {
  return (
    <section className="v5r-page">
      <PageHeader
        section="结果总览"
        subtitle="基于前哨 AI 胜任力模型生成"
        icon="bar-chart-3"
      />
      <MetaBar meta={vm.meta} />

      {/* 上半区：总体等级 + 一句话画像 + 能力摘要 */}
      <div className="v5r-p1-top" style={{ marginTop: 22 }}>
        <OverallCard vm={vm} />
        <div className="v5r-p1-right">
          <HeadlineCard headline={vm.overall.headline_tag} />
          <SummaryCard strengths={vm.strength_abilities} improvements={vm.improvement_abilities} />
        </div>
      </div>

      {/* 素质观察 */}
      <div className="v5r-card v5r-actions-card" style={{ marginBottom: 18 }}>
        <div className="v5r-card-title">
          <ReportIcon name="eye" size={16} /> 素质观察
        </div>
        <div className="v5r-trait-row">
          {vm.traits.map((t) => (
            <TraitCard key={t.code} trait={t} />
          ))}
        </div>
      </div>

      {/* 优先行动 */}
      <div className="v5r-card v5r-actions-card">
        <div className="v5r-card-title">
          <ReportIcon name="award" size={16} /> 优先行动
        </div>
        <div className="v5r-actions-chips">
          {vm.priority_actions.map((label, i) => (
            <div className="v5r-action-chip" key={i}>
              <ReportIcon name={['shield', 'clipboard', 'puzzle'][i] || 'circle'} size={18} />
              {label}
            </div>
          ))}
        </div>
        <div className="v5r-action-foot">
          <ReportIcon name="lightbulb" size={14} />
          建议先从低风险、高频任务开始练习。
        </div>
      </div>
    </section>
  );
}

function OverallCard({ vm }) {
  return (
    <div className="v5r-card v5r-overall-card">
      <div>
        <div className="v5r-card-title">
          <ReportIcon name="shield" size={16} /> 总体等级
        </div>
        <div className="v5r-l-row">
          <div className="v5r-l-grade">{vm.overall.l_grade || '—'}</div>
          <div className="v5r-l-name">{vm.overall.l_grade_label}</div>
        </div>
        <div className="v5r-score-row">
          综合得分:
          <span className="v5r-score-big">{vm.overall.percentage}</span>
          <span className="v5r-score-of">/ 100</span>
        </div>
      </div>
      {vm.overall.summary && (
        <div className="v5r-overall-tip">
          <ReportIcon name="lightbulb" size={16} />
          <span>{vm.overall.summary}</span>
        </div>
      )}
    </div>
  );
}

function HeadlineCard({ headline }) {
  return (
    <div className="v5r-card v5r-headline-card">
      <div className="v5r-card-title">
        <ReportIcon name="quote" size={16} /> 一句话画像
      </div>
      <div className="v5r-headline-text">{headline || '—'}</div>
    </div>
  );
}

function SummaryCard({ strengths, improvements }) {
  return (
    <div className="v5r-card">
      <div className="v5r-card-title">
        <ReportIcon name="bar-chart-3" size={16} /> 能力摘要
      </div>
      <div className="v5r-summary-grid">
        <div className="v5r-summary-col strength">
          <h4><ReportIcon name="thumbs-up" size={13} /> 优势能力</h4>
          <ul>
            {strengths.map((a) => (
              <li key={a.code}>{a.name}</li>
            ))}
          </ul>
        </div>
        <div className="v5r-summary-col improve">
          <h4><ReportIcon name="trending-up" size={13} /> 优先提升能力</h4>
          <ul>
            {improvements.map((a) => (
              <li key={a.code}>{a.name}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TraitCard({ trait }) {
  const tone = TIER_INTENSITY[trait.tier]?.tone || 'mid';
  return (
    <div className="v5r-trait-card">
      <div className="v5r-trait-head">
        <span className="v5r-trait-icon">
          <ReportIcon name={trait.icon} size={20} />
        </span>
        <div>
          <div className="v5r-trait-name">{trait.name}</div>
          <span className={`v5r-trait-tier tone-${tone}`}>{trait.tier}</span>
        </div>
      </div>
      <div className="v5r-trait-narrative">{trait.narrative || '—'}</div>
    </div>
  );
}

// ===========================================================================
// Page 2 · 能力结构
// ===========================================================================
function Page2({ vm }) {
  return (
    <section className="v5r-page">
      <PageHeader
        section="能力结构"
        subtitle="从八项核心能力出发，看清你的 AI 应用整体分层与图谱"
        icon="bar-chart"
      />
      <MetaBar meta={vm.meta} />
      <div className="v5r-p2-grid" style={{ marginTop: 22 }}>
        <div className="v5r-card">
          <div className="v5r-card-title">
            <ReportIcon name="bar-chart-3" size={16} /> 八力雷达图
          </div>
          <ReportV5Radar abilities={vm.abilities} />
        </div>
        <div className="v5r-card v5r-distrib-card">
          <div className="v5r-card-title">
            <ReportIcon name="bar-chart" size={16} /> 八力分布
          </div>
          {vm.abilities_by_layer.map((group) => (
            <LayerBlock key={group.layer} group={group} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LayerBlock({ group }) {
  return (
    <div className="v5r-layer-block">
      <div className="v5r-layer-name">{group.layer_name}</div>
      {group.abilities.map((a) => (
        <AbilityBar key={a.code} ability={a} />
      ))}
    </div>
  );
}

function AbilityBar({ ability }) {
  const grade = (ability.l_grade || 'L1').toLowerCase();
  return (
    <div className="v5r-ability-row">
      <span className="v5r-ability-name">{ability.name}</span>
      <span className={`v5r-l-chip ${grade}`}>{ability.l_grade || 'L?'}</span>
      <div className="v5r-bar">
        <div
          className="v5r-bar-fill"
          style={{ width: `${Math.max(0, Math.min(100, ability.percentage))}%` }}
        />
      </div>
      <div className="v5r-bar-pct">
        {ability.percentage} <span className="v5r-bar-of">/100</span>
      </div>
    </div>
  );
}

// ===========================================================================
// Page 3 · 行动建议
// ===========================================================================
function Page3({ vm }) {
  return (
    <section className="v5r-page">
      <PageHeader
        section="行动建议"
        subtitle="结合测评结果，给出适合你的成长路径、具体场景与提升建议"
        icon="award"
      />
      <MetaBar meta={vm.meta} />

      <div className="v5r-interp-card" style={{ marginTop: 22 }}>
        <div className="v5r-card-title">
          <ReportIcon name="book-open" size={16} /> 综合解读
        </div>
        <p className="v5r-interp-text">{vm.comprehensive_interpretation || '—'}</p>
      </div>

      <div className="v5r-scen-grid">
        <ScenarioCard
          variant="try-first"
          title="适合优先尝试的 AI 场景"
          icon="thumbs-up"
          items={vm.try_first_scenarios}
          footer="建议优先从低风险、高频、可复核的任务开始练习。"
        />
        <ScenarioCard
          variant="cautious"
          title="需要人工深度介入的场景"
          icon="shield"
          items={vm.be_cautious_scenarios}
          footer="涉及数据、事实、敏感信息和正式决策时，务必保留人工复核。"
        />
      </div>

      <div className="v5r-improvements">
        {vm.priority_improvements.map((it, i) => (
          <ImprovementCard key={i} item={it} num={i + 1} />
        ))}
      </div>
    </section>
  );
}

function ScenarioCard({ variant, title, icon, items, footer }) {
  return (
    <div className={`v5r-card v5r-scen-card ${variant}`}>
      <div className={`v5r-card-title ${variant}`}>
        <ReportIcon name={icon} size={16} /> {title}
      </div>
      <div className="v5r-scen-list">
        {items.map((s, i) => (
          <div key={i} className="v5r-scen-item">
            <ReportIcon name={s.icon_hint || 'circle'} size={16} />
            {s.name}
          </div>
        ))}
      </div>
      <div className="v5r-scen-foot">
        <ReportIcon name="lightbulb" size={13} />
        {footer}
      </div>
    </div>
  );
}

function ImprovementCard({ item, num }) {
  return (
    <div className="v5r-improv-card">
      <div className="v5r-improv-head">
        <span className="v5r-improv-num">0{num}</span>
        <span className="v5r-improv-title">{item.title}</span>
      </div>
      <div className="v5r-improv-block">
        <span className="v5r-improv-block-label">为什么要做</span>
        {item.why_to_do}
      </div>
      <div className="v5r-improv-block">
        <span className="v5r-improv-block-label">可以怎么做</span>
        {item.how_to_do}
      </div>
    </div>
  );
}

// ===========================================================================
// 报告底部：PDF 下载 + 详细报告 CTA（详细报告 M5 范围外，占位）
// ===========================================================================
function DetailedCTA({ unlocked, onUnlock, assessmentId }) {
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const previewMode = !assessmentId || assessmentId === 'preview' || assessmentId === 'demo';

  // 主下载从 PDF 改成 PNG 长图 (feat/pdf-playwright-frontend 2026-05-22):
  // PDF 分页断层不可控, PNG 无分页 100% 复刻前端视觉. PDF 老 endpoint 仍在,
  // admin / 老链接 / 企业脚本不破, 但前端 UI 只暴露 PNG 入口.
  // 粗略页只下载粗略 (variant=rough); 详细页有独立按钮 (variant=detailed).
  const handleDownload = async () => {
    if (previewMode || downloadBusy) return;
    setDownloadBusy(true);
    setDownloadError(null);
    const res = await downloadReportV5PDF(assessmentId, { variant: 'rough' });
    setDownloadBusy(false);
    if (!res.ok) setDownloadError(res.error || '报告下载失败');
  };

  return (
    <>
      <div className="v5r-pdf-row">
        <button
          type="button"
          className="v5r-pdf-btn"
          disabled={previewMode || downloadBusy}
          title={previewMode ? '预览模式不支持下载（无真实测评 id）' : ''}
          onClick={handleDownload}
        >
          <ReportIcon name="download" size={16} />
          {downloadBusy ? '正在生成报告…' : '下载 PDF 报告'}
        </button>
        {downloadError && <span className="v5r-pdf-error">{downloadError}</span>}
      </div>

      {unlocked ? (
        // 已解锁（backend 顶层 is_detailed_unlocked=true）—— "查看"按钮直接进 detailed 页
        <div className="v5r-cta unlocked">
          <div>
            <h3>详细报告已解锁</h3>
            <p>完整 6 页专业版报告已就绪 —— 含八力深度诊断、底层素质详解、岗位场景应用建议与 30 天提升路径。</p>
          </div>
          <button className="v5r-cta-btn" type="button" onClick={onUnlock}>
            <ReportIcon name="book-open" size={16} /> 查看详细报告
          </button>
        </div>
      ) : (
        // 未解锁 —— "解锁"按钮（真接口下 backend 会 403，付费流由独立 ticket 接入）
        <div className="v5r-cta">
          <div>
            <h3>想看更深入的解读？解锁详细报告</h3>
            <p>详细报告会按你的岗位与素质给出跨能力关联分析、底层素质详解、岗位场景应用建议与 30 天提升路径。</p>
          </div>
          <button className="v5r-cta-btn" type="button" onClick={onUnlock}>
            <ReportIcon name="award" size={16} /> 解锁详细报告
          </button>
        </div>
      )}
    </>
  );
}
